import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, Typography, Table, Tag, Select, Row, Col, Statistic, EmptyState, Space, Image, Avatar, type TagTone } from '../../ui';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { fmtQty } from '../../lib/format';
import { formatIst, formatIstDate } from '../../lib/time';

const TXN_COLOR: Record<string, TagTone> = {
  OPENING: 'green', GRN: 'green', TRANSFER_IN: 'cyan', RETURN_IN: 'cyan', ADJUST_IN: 'blue',
  SO: 'red', TRANSFER_OUT: 'gold', RETURN_OUT: 'gold', ADJUST_OUT: 'gold', PROD_IN: 'green', PROD_OUT: 'gold', SAMPLE: 'purple',
};
// Human labels for the raw ledger txn codes (kept in step with StockHistoryPage) — so the timeline
// reads "Sales Dispatch" instead of the internal "SO", "Sales Return In" instead of "RETURN_IN", etc.
const TXN_LABEL: Record<string, string> = {
  OPENING: 'Opening Stock', GRN: 'Goods Receipt (GRN)', SO: 'Sales Dispatch',
  TRANSFER_IN: 'Transfer In', TRANSFER_OUT: 'Transfer Out',
  ADJUST_IN: 'Adjustment In', ADJUST_OUT: 'Adjustment Out',
  RETURN_IN: 'Sales Return In', RETURN_OUT: 'Return Out',
  PROD_IN: 'Production In', PROD_OUT: 'Production Consume', SAMPLE: 'Sample Issue',
};
const txnLabel = (v: string) => TXN_LABEL[v] ?? (v ?? '').replace(/_/g, ' ');
const STATUS_COLOR: Record<string, TagTone> = { AVAILABLE: 'green', QC_HOLD: 'gold', EXPIRED: 'red', BLOCKED: 'red' };

/**
 * Trace any batch number end to end: where the stock currently sits (per warehouse), how much
 * reached each retailer, and the full movement timeline (master add → transfers → dispatch).
 */
export function BatchTracePage() {
  // A batch_no can recur across products, so a selection is (batchNo, productId) — encoded as the
  // Select value "productId|batchNo".
  const [selected, setSelected] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 350);
  const [productId, batchNo] = selected ? [selected.split('|')[0], selected.split('|').slice(1).join('|')] : [undefined, undefined];

  const { data: options } = useQuery({
    queryKey: ['batch-trace-options', search],
    queryFn: () => request<any[]>({ url: '/batch-trace/options', params: { q: search } }),
  });

  const { data, isFetching } = useQuery({
    queryKey: ['batch-trace', batchNo, productId],
    queryFn: () => request<any>({ url: '/batch-trace', params: { batchNo, productId } }),
    enabled: !!batchNo && !!productId,
  });

  return (
    <div>
      <Typography.Title level={4}>Batch Traceability</Typography.Title>
      <Card>
        <Space wrap style={{ marginBottom: 8 }}>
          <Select
            showSearch
            allowClear
            style={{ width: 440 }}
            placeholder="Search a batch number…"
            value={selected}
            onSearch={setSearchInput}
            onChange={(v) => setSelected(v)}
            filterOption={false}
            notFoundContent={search ? 'No matching batch' : 'Type to search'}
            options={(options ?? []).map((o: any) => ({ value: `${o.productId}|${o.batchNo}`, label: `${o.batchNo} · ${o.sku} ${o.productName}` }))}
          />
        </Space>

        {!batchNo && <EmptyState title="Search a batch number to trace it across warehouses and retailers" />}

        {batchNo && data && (
          <>
            {/* Product header — photo + key details for the traced item (a batch is product-scoped, so the
                first product is THE item). Falls back to an initial-letter avatar when there's no photo. */}
            {(() => {
              const p = (data.products ?? [])[0];
              if (!p) return null;
              return (
                <Card className="mb-4">
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {p.imageUrl ? (
                      <Image src={p.imageUrl} width={100} height={100} style={{ objectFit: 'cover', borderRadius: 8 }} preview />
                    ) : (
                      <Avatar size={100} style={{ background: '#1F8A4C', fontSize: 36, flexShrink: 0, borderRadius: 8 }} fallback={(p.productName ?? '?')[0]?.toUpperCase()} />
                    )}
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
                        {p.productName}
                        {p.variantLabel ? <Typography.Text type="secondary" style={{ fontWeight: 400 }}> · {p.variantLabel}</Typography.Text> : null}
                      </div>
                      <Space wrap size={[8, 4]} style={{ marginTop: 10 }}>
                        <Tag>SKU {p.sku}</Tag>
                        {p.hsnCode ? <Tag>HSN {p.hsnCode}</Tag> : null}
                        {Number(p.packSize) ? <Tag>Pack {fmtQty(p.packSize)} {p.uomCode}</Tag> : <Tag>UoM {p.uomCode}</Tag>}
                        {Number(p.boxPackingEnabled) && Number(p.unitsPerBox) > 0 ? <Tag tone="gold">Box of {p.unitsPerBox}</Tag> : null}
                        {Number(p.gstPct) > 0 ? <Tag>GST {Number(p.gstPct)}%</Tag> : null}
                      </Space>
                      {Number(p.dealerPrice) > 0 && (
                        <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 13 }}>
                          Dealer price ₹{Number(p.dealerPrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.4 }}>Batch / Lot</div>
                      <Tag tone="gold" style={{ marginTop: 4, marginRight: 0 }}>{data.batchNo}</Tag>
                    </div>
                  </div>
                </Card>
              );
            })()}
            {data.scoped && (
              <div className="mb-3 rounded-control border border-sky-500/25 bg-sky-500/10 px-3 py-2 text-xs text-content">
                Showing only the warehouse(s) you manage — on-hand, movements and the retailers you dispatched to. Admins see all warehouses.
              </div>
            )}
            <Row gutter={[12, 12]} style={{ margin: '4px 0 16px' }}>
              <Col xs={12} md={6}><Card><Statistic title={data.scoped ? 'On hand (your warehouse)' : 'On hand (all warehouses)'} value={fmtQty(data.summary.totalAvailable)} /></Card></Col>
              <Col xs={12} md={6}><Card><Statistic title={data.scoped ? 'Dispatched from you' : 'Dispatched to retailers'} value={fmtQty(data.summary.totalToRetailers)} /></Card></Col>
              <Col xs={12} md={6}><Card><Statistic title="Warehouses holding it" value={data.summary.warehouses} /></Card></Col>
              <Col xs={12} md={6}><Card><Statistic title="Retailers reached" value={data.summary.retailers} /></Card></Col>
            </Row>

            <Row gutter={[12, 12]}>
              <Col xs={24} lg={12}>
                <Card title="In warehouses (current on-hand)">
                  <Table size="small" rowKey={(_: any, i?: number) => String(i)} pagination={false} loading={isFetching} dataSource={data.byWarehouse ?? []}
                    locale={{ emptyText: 'Not in any warehouse' }}
                    columns={[
                      { title: 'Lot', dataIndex: 'batchNo', render: (v: string, r: any) => (
                        r.sourceBatchNo
                          ? <div><Tag tone="gold">Returned</Tag><div className="text-[11px] text-subtle" title="Internal system lot for returned/quarantined stock">{v}</div></div>
                          : <span className="font-medium">{v}</span>
                      ) },
                      { title: 'Warehouse', dataIndex: 'warehouseName', render: (v: string, r: any) => <Space>{r.warehouseCode} · {v}{Number(r.isMaster) ? <Tag tone="gold">Master</Tag> : null}</Space> },
                      { title: 'Available', dataIndex: 'available', align: 'right' as const, render: (v: any) => <b>{fmtQty(v)}</b> },
                      { title: 'Expiry', dataIndex: 'expDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
                      { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{(v ?? '').replace(/_/g, ' ')}</Tag> },
                    ]} />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Dispatched to retailers">
                  <Table size="small" rowKey={(_: any, i?: number) => String(i)} pagination={false} dataSource={data.byRetailer ?? []}
                    locale={{ emptyText: 'Not dispatched to any retailer yet' }}
                    columns={[
                      { title: 'Retailer', dataIndex: 'retailerName', render: (v: string, r: any) => `${r.retailerCode ? r.retailerCode + ' · ' : ''}${v}` },
                      { title: 'Order', dataIndex: 'orderNo', render: (v: string, r: any) => (r.orderId ? <Link to={`/orders/${r.orderId}`} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{v}</Link> : (v ?? '—')) },
                      { title: 'Dispatched', dataIndex: 'dispatchedAt', render: (v: any) => (v ? formatIstDate(v) : '—') },
                      { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => <b>{fmtQty(v)}</b> },
                    ]} />
                </Card>
              </Col>
              <Col xs={24}>
                <Card title="Movement history">
                  <Table size="small" rowKey="id" pagination={{ pageSize: 25 }} dataSource={data.timeline ?? []}
                    locale={{ emptyText: 'No movements' }}
                    columns={[
                      { title: 'When', dataIndex: 'txnDate', render: (v: string) => formatIst(v) },
                      { title: 'Type', dataIndex: 'txnType', render: (v: string) => <Tag tone={TXN_COLOR[v] ?? 'neutral'}>{txnLabel(v)}</Tag> },
                      { title: 'Warehouse', dataIndex: 'warehouseCode', render: (v: any) => v ?? '—' },
                      { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => { const n = Number(v); return <span style={{ color: n < 0 ? '#cf1322' : '#389e0d' }}>{n > 0 ? '+' : ''}{fmtQty(v)}</span>; } },
                      { title: 'Balance', dataIndex: 'runningBalance', align: 'right' as const, render: (v: any) => fmtQty(v) },
                      { title: 'Ref', render: (_: any, r: any) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.refNo ?? (r.refTable ? String(r.refTable).replace(/_/g, ' ') : '—')}</Typography.Text> },
                    ]} />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
}
